package gr.tech.site.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.components.layouts.PageLayoutData
import gr.tech.site.components.sections.PartnersSectionAnimatedCompact
import gr.tech.site.components.sections.SalesforceContactForm
import gr.tech.site.styles.*
import gr.tech.site.util.bearHuntingForm
import gr.tech.site.util.porridgeTimer
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

@InitRoute
fun initContactUsPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(
        showContactHeader = true,
        title = "Contact Us — Get Your Free Rebate Estimate",
        description = "No account needed. Tell us about your facility and our team will find every rebate you qualify for. No signup, no portal — just results.",
        canonicalUrl = "/contact-us",
        ogImage = "/images/ledgrowlights.jpg"
    ))
}

@Page()
@Layout(".components.layouts.PageLayout")
@Composable
fun ContactUsPage() {
    val context = rememberPageContext()
    porridgeTimer.startTimer()

    val leadSource = context.route.params["source"] ?: "Green Rebates Website Contact Form"
    val stateParam = context.route.params["state"] ?: ""
    val utilParam = context.route.params["utility"] ?: ""

    Section(
        attrs = Modifier
            .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
            .wideCappedWidth()
            .backgroundColor(Colors.White)
            .toAttrs()
    ) {
        Div(attrs = Modifier.classNames("container").toAttrs()) {
            bearHuntingForm()

            H2(attrs = ContactUsHeader.toModifier().toAttrs()) {
                Text("Start Your Rebate Estimate")
            }
            P(attrs = ContactUsDescription.toModifier().toAttrs()) {
                Text("No sign up needed. Share a few details about your facility and our team will get started.")
            }


            // Two-column layout: form left, contact info right.
            // On mobile, contact info stacks below the form (natural DOM order).
            Div(attrs = Modifier.classNames("row", "g-5", "align-items-start").toAttrs()) {

                // LEFT — Contact Form
                Div(attrs = Modifier.classNames("col-12", "col-lg-8").toAttrs()) {
                    SalesforceContactForm(
                        leadSource = leadSource,
                        state = stateParam,
                        util = utilParam
                    )
                }

                // RIGHT — Contact Info Panel
                // Text content uses Width.FitContent + mx-auto: shrinks to widest item and centers.
                // Slider is a sibling outside that wrapper so it fills the full column width.
                Div(attrs = ContactInfoDividerStyle.toModifier().classNames("col-12", "col-lg-4", "py-2", "pb-5").toAttrs()) {

                    Div(attrs = Modifier.classNames("mx-auto", "d-flex", "flex-column").toAttrs()) {

                        H4(attrs = ContactInfoTitleStyle.toModifier().toAttrs()) {
                            Text("Chat With Us")
                        }
                        P(attrs = ContactInfoSubtextStyle.toModifier().toAttrs()) {
                            Text("Message our awesome team members")
                        }

                        // Email
                        Link(
                            path = "mailto:info@greenrebates.com",
                            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                            modifier = Modifier.classNames("d-flex", "align-items-center", "gap-2", "mb-3")
                        ) {
                            FaEnvelope(modifier = ContactInfoIconStyle.toModifier(), size = IconSize.SM)
                            Span(attrs = ContactInfoLinkTextStyle.toModifier().toAttrs()) {
                                Text("info@greenrebates.com")
                            }
                        }

                        // Socials
                        Div(attrs = Modifier.classNames("d-flex", "flex-column", "gap-3", "mb-5").toAttrs()) {
                            listOf(
                                Triple("https://www.facebook.com/greenrebates/",  "facebook.com/greenrebates",  0),
                                Triple("https://www.instagram.com/greenrebates/", "instagram.com/greenrebates", 1),
                                Triple("https://x.com/greenrebates1",             "x.com/greenrebates",        2),
                            ).forEach { (url, displayText, iconIndex) ->
                                Link(
                                    path = url,
                                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                                    modifier = Modifier.classNames("d-flex", "align-items-center", "gap-2"),
                                    openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB
                                ) {
                                    when (iconIndex) {
                                        0    -> FaFacebook(modifier = ContactInfoIconStyle.toModifier(), size = IconSize.SM)
                                        1    -> FaInstagram(modifier = ContactInfoIconStyle.toModifier(), size = IconSize.SM)
                                        2    -> FaXTwitter(modifier = ContactInfoIconStyle.toModifier(), size = IconSize.SM)
                                    }
                                    Span(attrs = ContactInfoLinkTextStyle.toModifier().toAttrs()) {
                                        Text(displayText)
                                    }
                                }
                            }
                        }

                        H4(attrs = ContactInfoTitleStyle.toModifier().toAttrs()) {
                            Text("Call Us")
                        }
                        P(attrs = ContactInfoSubtextStyle.toModifier().toAttrs()) {
                            Text("Call our team Mon-Fri 8 AM - 6 PM")
                        }

                        // Phone
                        Link(
                            path = "tel:8002081455",
                            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                            modifier = Modifier.classNames("d-flex", "align-items-center", "gap-2", "mb-5")
                        ) {
                            FaPhone(modifier = ContactInfoIconStyle.toModifier(), size = IconSize.XS)
                            Span(attrs = ContactInfoLinkTextStyle.toModifier().toAttrs()) {
                                Text("800-208-1455")
                            }
                        }

                        H4(attrs = ContactInfoTitleStyle.toModifier().classNames("mb-1").toAttrs()) {
                            Text("Trusted by 50+ partners!")
                        }
                        P(attrs = ContactInfoSubtextStyle.toModifier().classNames("mb-0").toAttrs()) {
                            Text("Chat with us, and you'll understand.")
                        }
                        PartnersSectionAnimatedCompact()

                    } // end fit-content text wrapper
                }
            }
        }
    }
}
